import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { IFilterItemV2 } from "#src/commonV2/Table/Filter/types";
import { IFormDropdownProps } from "./FormDropdown";

interface IUseFormDropdownProps {
  id?: string;
  label: string;
  defaultSelected: IFilterItemV2;
  options: IFilterItemV2[];
  hint?: ReactNode;
  requiredMark?: boolean;
}

export const useFormDropdown = ({
  id,
  label,
  defaultSelected,
  options,
  hint,
  requiredMark,
}: IUseFormDropdownProps) => {
  // --------------- STATES ---------------

  const [selected, setSelected] = useState<IFilterItemV2>(defaultSelected);

  // --------------- METHODS ---------------

  const onChangeSelected = useCallback((value: IFilterItemV2) => {
    setSelected(value);
  }, []);

  const reset = () => {
    setSelected(defaultSelected);
  };

  // --------------- COMPUTED ---------------

  const wasChanged = useMemo(
    () => selected.value !== defaultSelected.value,
    [selected, defaultSelected]
  );

  const renderProps = useMemo(
    () =>
      ({
        id,
        label,
        requiredMark,
        hint,
        options,
        selected,
        onChangeSelected,
      }) satisfies IFormDropdownProps,
    [id, label, requiredMark, hint, options, selected, onChangeSelected]
  );

  // --------------- EFFECTS ---------------

  useEffect(() => {
    setSelected(defaultSelected);
  }, [defaultSelected]);

  return {
    selected,
    reset,
    wasChanged,
    renderProps,
  };
};
